import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Badge,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { QuestionAnswer, Close } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getQuestions, ApiQuestion, answerQuestion, updateAnswer } from '../../api/questionsService';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { DESIGN_TOKENS } from '../../styles/constants';

// Styled components matching Figma design
const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '40px',
  gap: '24px',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
  '@media (max-width: 1200px)': {
    padding: '40px',
  },
  '@media (max-width: 768px)': {
    padding: '20px',
  },
});

const PageHeader = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const Title = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.h1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.tight,
  letterSpacing: '-0.44px',
});

const Subtitle = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const TabContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '4px',
  },
});

const StyledTab = styled(Paper)(({ active }: { active: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px 7px',
  height: '39px',
  borderRadius: '16px',
  backgroundColor: active ? '#AC654D' : '#F6F8F8',
  cursor: 'pointer',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: active ? '#AC654D' : '#F1F4F4',
  },
}));

const TabText = styled(Typography)(({ active }: { active: boolean }) => ({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: active ? '#FFFFFF' : DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
}));

const QuestionCard = styled(Paper)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '24px 0px',
  borderBottom: '1px solid #F1F4F4',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  borderRadius: 0,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#FAFAFA',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
});

const QuestionContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: 1,
});

const QuestionHeader = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const QuestionMeta = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
});

const QuestionAuthor = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.caption,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const QuestionDate = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.caption,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.textSecondary,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const QuestionText = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const AnswerSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginTop: '16px',
});

const AnswerMeta = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
});

const AnswerAuthor = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.caption,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const AnswerDate = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.caption,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.textSecondary,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const AnswerText = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const getActionButtonStyles = (variant: 'primary' | 'secondary') => ({
  width: '160px',
  height: '56px',
  borderRadius: '16px',
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
  textTransform: 'none' as const,
  letterSpacing: '-0.32px',
  ...(variant === 'primary' ? {
    backgroundColor: DESIGN_TOKENS.colors.primary,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: DESIGN_TOKENS.colors.primaryDark,
    },
  } : {
    backgroundColor: 'transparent',
    color: DESIGN_TOKENS.colors.primary,
    border: `1px solid ${DESIGN_TOKENS.colors.primary}`,
    '&:hover': {
      backgroundColor: 'rgba(103, 49, 79, 0.04)',
    },
  }),
});

const PaginationContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '32px',
});

const PaginationInfo = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.textSecondary,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
});

const PaginationButtons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const PaginationButton = styled(Button)(({ active }: { active: boolean }) => ({
  minWidth: '56px',
  height: '56px',
  borderRadius: '60px',
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: active ? DESIGN_TOKENS.typography.fontWeights.bold : DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.text,
  backgroundColor: active ? '#F4DBC5' : 'transparent',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: active ? '#F4DBC5' : '#F1F4F4',
  },
}));

const EmptyState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '32px',
  padding: '40px 24px',
  flex: 1,
  minHeight: '400px',
});

const EmptyIcon = styled(Box)({
  width: '120px',
  height: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.5,
  position: 'relative',
});

const EmptyTitle = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.h3,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.normal,
  letterSpacing: '-0.11px',
  textAlign: 'center',
});

const EmptyDescription = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.text,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
  textAlign: 'center',
});

// Component Types
interface TransformedQuestion {
  id: string;
  author: string;
  date: string;
  question: string;
  answered: boolean;
  answer?: string;
  answerDate?: string;
}

// Utility function to format date
const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day} ${month}, ${hours}:${minutes}`;
};

// Transform API data to component format
const transformQuestion = (apiQuestion: ApiQuestion): TransformedQuestion => ({
  id: apiQuestion.questionId,
  author: apiQuestion.userName,
  date: formatDate(apiQuestion.questionTimestamp),
  question: apiQuestion.question,
  answered: apiQuestion.isAnswered,
  answer: apiQuestion.answer || undefined,
  answerDate: apiQuestion.answerTimestamp ? formatDate(apiQuestion.answerTimestamp) : undefined,
});

const Questions: React.FC = () => {
  const { vendor } = useVendorAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const questionIdRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [activeTab, setActiveTab] = useState(0);
  const [selectedQuestion, setSelectedQuestion] = useState<TransformedQuestion | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // API state
  const [questions, setQuestions] = useState<TransformedQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch questions from API
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!vendor?.vendorId) {
        setError('Vendor ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Get questionId from URL query parameter
        const questionId = searchParams.get('questionId');
        
        console.log('Fetching questions:', { vendorId: vendor.vendorId, questionId });
        
        const response = await getQuestions(vendor.vendorId, questionId || undefined);
        
        console.log('Questions API response:', response);
        
        if (response && response.success && response.data) {
          const transformedQuestions = response.data.map((q) => transformQuestion(q));
          setQuestions(transformedQuestions);
          // setTotalCount(response.count || 0); // Not currently used
        } else {
          throw new Error(response?.message || 'API returned unsuccessful response');
        }
      } catch (err) {
        console.error('Error fetching questions:', err);
        
        // Handle axios errors with better messages
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Unable to connect to the server. Please make sure the API server is running on http://localhost:3002');
          } else if (err.response) {
            setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
          } else if (err.request) {
            setError('No response from server. Please check if the API is running.');
          } else {
            setError(err.message);
          }
        } else {
          setError(err instanceof Error ? err.message : 'Failed to fetch questions');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [vendor?.vendorId, searchParams]);

  // Calculate counts for tabs
  const unansweredCount = questions.filter(q => !q.answered).length;

  const tabs = [
    { label: 'All questions', count: null },
    { label: 'New', count: unansweredCount },
    { label: 'Answered', count: null },
  ];

  const filteredQuestions = questions.filter(question => {
    if (activeTab === 0) return true; // All questions
    if (activeTab === 1) return !question.answered; // New
    if (activeTab === 2) return question.answered; // Answered
    return true;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedQuestions = filteredQuestions.slice(startIndex, endIndex);

  // Reset to page 1 when changing tabs
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Scroll to question if questionId is in URL (after questions are loaded)
  useEffect(() => {
    const questionId = searchParams.get('questionId');
    if (questionId && questions.length > 0 && !loading) {
      // Scroll to the question after a short delay to allow page to render
      setTimeout(() => {
        const questionElement = questionIdRef.current[questionId];
        if (questionElement) {
          questionElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);
    }
  }, [questions, loading, searchParams]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };


  const handleAnswerQuestion = (question: TransformedQuestion) => {
    setSelectedQuestion(question);
    setAnswerText('');
    setIsAnswerModalOpen(true);
  };

  const handleEditAnswer = (question: TransformedQuestion) => {
    setSelectedQuestion(question);
    setAnswerText(question.answer || '');
    setIsAnswerModalOpen(true);
  };

  const handleSubmitAnswer = async () => {
    if (!vendor?.vendorId || !selectedQuestion || !answerText.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const isEditing = selectedQuestion.answered;
      
      if (isEditing) {
        // Update existing answer
        await updateAnswer({
          vendorId: vendor.vendorId,
          questionId: selectedQuestion.id.toString(),
          answer: answerText.trim(),
        });
        toast.success('Answer updated successfully');
      } else {
        // Create new answer
        await answerQuestion({
          vendorId: vendor.vendorId,
          questionId: selectedQuestion.id.toString(),
          answer: answerText.trim(),
        });
        toast.success('Answer submitted successfully');
      }

      // Update local state
      setQuestions(prevQuestions =>
        prevQuestions.map(q =>
          q.id === selectedQuestion.id
            ? {
                ...q,
                answered: true,
                answer: answerText.trim(),
                answerDate: formatDate(new Date().toISOString()),
              }
            : q
        )
      );

      setIsAnswerModalOpen(false);
      setAnswerText('');
      setSelectedQuestion(null);
    } catch (err) {
      console.error('Error submitting answer:', err);
      if (axios.isAxiosError(err)) {
        toast.error(err.response?.data?.message || 'Failed to submit answer');
      } else {
        toast.error('Failed to submit answer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsAnswerModalOpen(false);
    setAnswerText('');
    setSelectedQuestion(null);
  };

  return (
    <PageContainer>
      <MainContent>
        <PageHeader>
          <Title>Questions</Title>
          <Subtitle>Answer users questions on your services.</Subtitle>
        </PageHeader>

        <TabContainer>
          {tabs.map((tab, index) => (
            <StyledTab
              key={index}
              active={activeTab === index}
              onClick={() => setActiveTab(index)}
            >
              <TabText active={activeTab === index}>{tab.label}</TabText>
              {tab.count !== null && tab.count > 0 && (
                <Badge
                  badgeContent={tab.count}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#426A53',
                      color: '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: 700,
                      height: '16px',
                      minWidth: '16px',
                    },
                  }}
                />
              )}
            </StyledTab>
          ))}
        </TabContainer>

        {/* Loading State */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px' 
          }}>
            <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary }} />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: '12px',
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Filter Indicator */}
        {!loading && searchParams.get('questionId') && (
          <Alert 
            severity="info" 
            sx={{ 
              borderRadius: '12px',
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            action={
              <Button
                size="small"
                onClick={() => {
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.delete('questionId');
                    return newParams;
                  });
                }}
                sx={{
                  color: DESIGN_TOKENS.colors.primary,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Show All Questions
              </Button>
            }
          >
            Showing filtered question
          </Alert>
        )}

        {/* Content */}
        {!loading && !error && filteredQuestions.length === 0 ? (
          searchParams.get('questionId') ? (
            <EmptyState>
              <EmptyIcon>
                <Box sx={{ 
                  position: 'relative',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {/* Background decorative elements */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0.5,
                    background: 'linear-gradient(135deg, #F4DBC5 0%, #F6F8F8 100%)',
                    borderRadius: '50%',
                  }} />
                  <QuestionAnswer sx={{ 
                    fontSize: 60, 
                    color: '#67314F',
                    zIndex: 1,
                    position: 'relative',
                  }} />
                </Box>
              </EmptyIcon>
              <Box sx={{ textAlign: 'center' }}>
                <EmptyTitle>Question not found</EmptyTitle>
                <EmptyDescription>Couldn't find any question with ID: {searchParams.get('questionId')}</EmptyDescription>
              </Box>
            </EmptyState>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <Box sx={{ 
                  position: 'relative',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {/* Background decorative elements */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0.5,
                    background: 'linear-gradient(135deg, #F4DBC5 0%, #F6F8F8 100%)',
                    borderRadius: '50%',
                  }} />
                  <QuestionAnswer sx={{ 
                    fontSize: 60, 
                    color: '#67314F',
                    zIndex: 1,
                    position: 'relative',
                  }} />
                </Box>
              </EmptyIcon>
              <Box sx={{ textAlign: 'center' }}>
                <EmptyTitle>No questions</EmptyTitle>
                <EmptyDescription>There are no questions to display yet.</EmptyDescription>
              </Box>
            </EmptyState>
          )
        ) : !loading && !error && (
          <Box>
            {paginatedQuestions.map((question) => (
              <QuestionCard 
                key={question.id}
                ref={(el: HTMLDivElement | null) => {
                  questionIdRef.current[question.id] = el;
                }}
              >
                <QuestionContent>
                  <QuestionHeader>
                    <QuestionMeta>
                      <QuestionAuthor>{question.author}</QuestionAuthor>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <QuestionDate>{question.date}</QuestionDate>
                        <Typography
                          sx={{
                            fontFamily: DESIGN_TOKENS.typography.fontFamily,
                            fontSize: '11px',
                            fontWeight: 400,
                            color: DESIGN_TOKENS.colors.textSecondary,
                            opacity: 0.7,
                          }}
                        >
                          ID: {question.id}
                        </Typography>
                      </Box>
                    </QuestionMeta>
                    <QuestionText>{question.question}</QuestionText>
                  </QuestionHeader>

                  {question.answered && (
                    <AnswerSection>
                      <AnswerMeta>
                        <AnswerAuthor>Your answer</AnswerAuthor>
                        <AnswerDate>{question.answerDate}</AnswerDate>
                      </AnswerMeta>
                      <AnswerText>{question.answer}</AnswerText>
                    </AnswerSection>
                  )}
                </QuestionContent>

                <Button
                  sx={getActionButtonStyles(question.answered ? 'secondary' : 'primary')}
                  onClick={() => 
                    question.answered 
                      ? handleEditAnswer(question) 
                      : handleAnswerQuestion(question)
                  }
                >
                  {question.answered ? 'Edit answer' : 'Answer'}
                </Button>
              </QuestionCard>
            ))}

            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationInfo>
                  {filteredQuestions.length > 0 
                    ? `${startIndex + 1}–${Math.min(endIndex, filteredQuestions.length)} of ${filteredQuestions.length} ${searchParams.get('questionId') ? 'question' : 'questions'}`
                    : '0 questions'
                  }
                </PaginationInfo>
                <PaginationButtons>
                  {/* Previous Button */}
                  <PaginationButton 
                    active={false}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    sx={{
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      ←
                    </Box>
                    Prev
                  </PaginationButton>

                  {/* Page Numbers */}
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <Typography 
                        key={`ellipsis-${index}`} 
                        sx={{ px: 2, color: DESIGN_TOKENS.colors.text }}
                      >
                        ...
                      </Typography>
                    ) : (
                      <PaginationButton
                        key={page}
                        active={currentPage === page}
                        onClick={() => handlePageChange(page as number)}
                      >
                        {page}
                      </PaginationButton>
                    )
                  ))}

                  {/* Next Button */}
                  <PaginationButton 
                    active={false}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    sx={{
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Next
                    <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                      →
                    </Box>
                  </PaginationButton>
                </PaginationButtons>
              </PaginationContainer>
            )}
          </Box>
        )}

        {/* Answer Modal */}
        <Dialog
          open={isAnswerModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
              maxHeight: '90vh',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: DESIGN_TOKENS.typography.fontSizes.h3,
            fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
            color: DESIGN_TOKENS.colors.text,
          }}>
            Answer
            <Button
              onClick={handleCloseModal}
              sx={{
                minWidth: 'auto',
                width: '40px',
                height: '40px',
                borderRadius: '40px',
                backgroundColor: '#F6F8F8',
                '&:hover': {
                  backgroundColor: '#F1F4F4',
                },
              }}
            >
              <Close />
            </Button>
          </DialogTitle>

          <DialogContent sx={{ 
            padding: '24px',
            '&:first-of-type': {
              paddingTop: '24px',
            },
          }}>
            <Box sx={{ mb: 3 }}>
              <QuestionMeta sx={{ mb: 1 }}>
                <QuestionAuthor>{selectedQuestion?.author} asked on</QuestionAuthor>
                <QuestionDate>{selectedQuestion?.date}</QuestionDate>
              </QuestionMeta>
              <QuestionText>{selectedQuestion?.question}</QuestionText>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={6}
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Your answer"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F6F8F8',
                  borderRadius: '12px',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: '1px solid #67314F',
                  },
                },
                '& .MuiInputBase-input': {
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
                  color: DESIGN_TOKENS.colors.text,
                },
              }}
            />
            <Typography sx={{ 
              mt: 1, 
              fontSize: DESIGN_TOKENS.typography.fontSizes.caption,
              color: DESIGN_TOKENS.colors.textSecondary,
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
            }}>
              Your answer will be visible to all users
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={handleCloseModal}
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
                fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
                color: DESIGN_TOKENS.colors.primary,
                textTransform: 'none',
                letterSpacing: '-0.32px',
              }}
            >
              Cancel
            </Button>
            <Button
              sx={getActionButtonStyles('primary')}
              onClick={handleSubmitAnswer}
              disabled={!answerText.trim() || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>
      </MainContent>
    </PageContainer>
  );
};

export default Questions;
